import { ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { buildRequestErrorMessage, formatFileSize, getPathFileName, isWindowsAbsolutePath } from './shared';

const DOWNLOAD_LIMIT_ROOT = 'C:\\mazda';

function normalizeWindowsPath(path) {
  return String(path || '')
    .replace(/\//g, '\\')
    .replace(/\\+/g, '\\')
    .trim()
    .replace(/\\+$/, '')
    .toLowerCase();
}

function isPathUnderDownloadRoot(path) {
  const normalizedPath = normalizeWindowsPath(path);
  const normalizedRoot = normalizeWindowsPath(DOWNLOAD_LIMIT_ROOT);
  return normalizedPath === normalizedRoot || normalizedPath.startsWith(`${normalizedRoot}\\`);
}

function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'download.bin';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function tryReadBlobError(blob) {
  try {
    if (!(blob instanceof Blob)) {
      return '';
    }
    const text = await blob.text();
    if (!text) {
      return '';
    }
    try {
      const json = JSON.parse(text);
      return json?.message || json?.detail || text;
    } catch (_) {
      return text;
    }
  } catch (_) {
    return '';
  }
}

export function useMazdaReplace(mazdaPost) {
  const targetReplaceFilePath = ref('C:\\mazda\\tool_MAZDA.exe');
  const editPath = ref('');
  const dialogExchangeFileName = ref(false);
  const uploadfiletest = ref(null);
  const oldFile = ref(null);
  const loading = ref(false);
  const queryFileSizeLoading = ref(false);
  const downloadLoading = ref(false);
  const targetFileSizeQueried = ref(false);
  const targetFileExists = ref(false);
  const targetFileSizeBytes = ref(0);
  const targetFileQueryMessage = ref('');

  watch(targetReplaceFilePath, () => {
    targetFileSizeQueried.value = false;
    targetFileExists.value = false;
    targetFileSizeBytes.value = 0;
    targetFileQueryMessage.value = '';
  });

  const showReplaceFileDialog = () => {
    editPath.value = targetReplaceFilePath.value;
    dialogExchangeFileName.value = true;
  };

  const confirmReplaceFile = () => {
    targetReplaceFilePath.value = editPath.value;
    dialogExchangeFileName.value = false;
  };

  const cancelInputReplacePath = () => {
    dialogExchangeFileName.value = false;
  };

  const replaceFile = (file) => {
    oldFile.value = file.raw;
  };

  const replaceFileTest = (files) => {
    ElMessage.warning(`最多只能上传1个文件，您选择了${files.length}个文件，请删除部分文件后重新上传！`);
  };

  const clearReplaceFile = () => {
    oldFile.value = null;
    if (uploadfiletest.value) {
      uploadfiletest.value.clearFiles();
    }
  };

  const ensureValidTargetPath = () => {
    if (!targetReplaceFilePath.value) {
      ElMessage.warning('请输入目标文件完整路径');
      return false;
    }
    if (!isWindowsAbsolutePath(targetReplaceFilePath.value)) {
      ElMessage.error('目标文件路径必须是完整绝对路径');
      return false;
    }
    return true;
  };

  const queryCurrentTargetFileSize = async () => {
    if (!ensureValidTargetPath()) {
      return;
    }
    if (!isPathUnderDownloadRoot(targetReplaceFilePath.value)) {
      ElMessage.error(`查看/下载只允许 ${DOWNLOAD_LIMIT_ROOT} 目录下的文件`);
      return;
    }

    queryFileSizeLoading.value = true;
    try {
      const response = await mazdaPost('queryTargetFileSize', {
        targetPath: targetReplaceFilePath.value,
      }, {
        timeout: 15000,
      });

      const data = response?.data || {};
      targetFileSizeQueried.value = true;
      targetFileExists.value = !!data.exists;
      targetFileSizeBytes.value = Number(data.size || 0);
      targetFileQueryMessage.value = data.message || '';

      if (data.success) {
        if (data.exists) {
          ElMessage.success(`当前目标文件大小：${formatFileSize(Number(data.size || 0))}`);
        } else {
          ElMessage.warning(data.message || '目标文件不存在');
        }
      } else {
        ElMessage.error(data.message || '查询目标文件大小失败');
      }
    } catch (error) {
      targetFileSizeQueried.value = false;
      ElMessage.error(buildRequestErrorMessage('查询目标文件大小失败', error));
    } finally {
      queryFileSizeLoading.value = false;
    }
  };

  const downloadTargetFile = async () => {
    if (!ensureValidTargetPath()) {
      return;
    }
    if (!isPathUnderDownloadRoot(targetReplaceFilePath.value)) {
      ElMessage.error(`查看/下载只允许 ${DOWNLOAD_LIMIT_ROOT} 目录下的文件`);
      return;
    }

    downloadLoading.value = true;
    try {
      const response = await mazdaPost('downloadTargetFile', {
        targetPath: targetReplaceFilePath.value,
      }, {
        timeout: 60000,
        responseType: 'blob',
      });

      const blob = response?.data;
      const contentType = response?.headers?.['content-type'] || '';
      if (String(contentType).includes('application/json')) {
        const text = await tryReadBlobError(blob);
        ElMessage.error(text || '下载目标文件失败');
        return;
      }

      const fileName = getPathFileName(targetReplaceFilePath.value) || 'download.bin';
      triggerBlobDownload(blob, fileName);
      ElMessage.success('目标文件下载成功');
    } catch (error) {
      let msg = buildRequestErrorMessage('下载目标文件失败', error);
      const data = error?.response?.data;
      if (data instanceof Blob) {
        const text = await tryReadBlobError(data);
        if (text) {
          msg = `下载目标文件失败: ${text}`;
        }
      }
      ElMessage.error(msg);
    } finally {
      downloadLoading.value = false;
    }
  };

  const submitUpload = async () => {
    if (!oldFile.value) {
      ElMessage.warning('请先选择要上传替换的文件');
      return;
    }
    if (!ensureValidTargetPath()) {
      return;
    }

    const targetName = getPathFileName(targetReplaceFilePath.value);
    if (!targetName) {
      ElMessage.error('目标文件路径无效');
      return;
    }
    if (targetName !== oldFile.value.name) {
      ElMessage.error('目标文件名必须与上传文件名一致');
      return;
    }

    loading.value = true;
    try {
      const formData = new FormData();
      formData.append('file', oldFile.value);
      formData.append('targetPath', targetReplaceFilePath.value);

      const response = await mazdaPost('replaceFile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });

      if (response.data?.success) {
        ElMessage.success(response.data.message || '文件替换成功');
        targetFileSizeQueried.value = false;
        clearReplaceFile();
      } else {
        ElMessage.error(response.data?.message || '文件替换失败');
      }
    } catch (error) {
      ElMessage.error(buildRequestErrorMessage('文件替换失败', error));
    } finally {
      loading.value = false;
    }
  };

  return {
    targetReplaceFilePath,
    editPath,
    dialogExchangeFileName,
    uploadfiletest,
    oldFile,
    loading,
    queryFileSizeLoading,
    downloadLoading,
    targetFileSizeQueried,
    targetFileExists,
    targetFileSizeBytes,
    targetFileQueryMessage,
    formatFileSize,
    replaceFile,
    replaceFileTest,
    clearReplaceFile,
    showReplaceFileDialog,
    confirmReplaceFile,
    cancelInputReplacePath,
    queryCurrentTargetFileSize,
    downloadTargetFile,
    submitUpload,
  };
}
